import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { formatDate, getStatusColor } from '../lib/format';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, Briefcase, ChevronRight, Mail } from 'lucide-react';
import OpenInvoicesEmailDialog from '../components/OpenInvoicesEmailDialog';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [invoiceProject, setInvoiceProject] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  const fetchProjects = () => {
    api.get('/projects').then(r => {
      setProjects(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchProjects(); }, []);

  useEffect(() => {
    // Support "create project from client" deep link without invalid /projects/new route.
    if (searchParams.get('new') === '1') {
      setShowCreate(true);
    }
  }, [searchParams]);

  const filteredProjects = useMemo(() => {
    let list = projects;

    if (statusFilter === 'active') {
      list = list.filter((p) => p.status === 'active');
    } else if (statusFilter === 'completed') {
      list = list.filter((p) => p.status === 'completed');
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.client?.name || '').toLowerCase().includes(q)
      );
    }

    const statusOrder = { active: 0, completed: 1, on_hold: 2, cancelled: 3 };
    return [...list].sort((a, b) => {
      const statusDiff = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [projects, statusFilter, searchQuery]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="projects-title">Projects</h1>
          <p className="text-sm text-slate-500 mt-1">Manage all your projects</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowCreate(true)} data-testid="add-project-button">
          <Plus className="w-4 h-4 mr-2" /> New Project
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search projects or clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="projects-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="projects-status-filter" className="text-sm text-slate-600 whitespace-nowrap">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger id="projects-status-filter" className="w-[160px]" data-testid="projects-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : projects.length === 0 ? (
            <div className="p-8 text-center">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No projects yet. Create one to get started.</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="p-8 text-center">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No projects match your search or filter.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Open invoices</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/projects/${p.id}`)}>
                    <TableCell>
                      <p className="font-medium text-slate-900">{p.name}</p>
                      {p.description && <p className="text-xs text-slate-500 truncate max-w-xs">{p.description}</p>}
                    </TableCell>
                    <TableCell className="text-slate-600">{p.client?.name || '-'}</TableCell>
                    <TableCell><Badge className={`text-xs ${getStatusColor(p.status)}`}>{p.status}</Badge></TableCell>
                    <TableCell className="text-slate-500 text-sm">{formatDate(p.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(event) => { event.stopPropagation(); setInvoiceProject(p); }}
                        title="Review and email open invoices"
                      >
                        <Mail className="mr-2 h-4 w-4" /> Open invoices
                      </Button>
                    </TableCell>
                    <TableCell><ChevronRight className="w-4 h-4 text-slate-400" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateProjectDialog
        open={showCreate}
        defaultClientId={searchParams.get('client_id') || ''}
        onClose={() => setShowCreate(false)}
        onCreated={() => { setShowCreate(false); fetchProjects(); }}
      />
      <OpenInvoicesEmailDialog
        open={Boolean(invoiceProject)}
        onOpenChange={(open) => { if (!open) setInvoiceProject(null); }}
        scopeType="project"
        scope={invoiceProject}
      />
    </div>
  );
}

function CreateProjectDialog({ open, onClose, onCreated, defaultClientId = '' }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ 
    client_id: '', 
    name: '', 
    description: '', 
    status: 'active',
    default_client_po_number: '',
    default_client_ticket_number: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/clients').then(r => setClients(r.data));
    }
  }, [open]);

  useEffect(() => {
    if (open && defaultClientId) {
      setForm((prev) => ({ ...prev, client_id: defaultClientId }));
    }
  }, [open, defaultClientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.name) { toast.error('Client and project name required'); return; }
    setSaving(true);
    try {
      await api.post('/projects', form);
      toast.success('Project created');
      setForm({ 
        client_id: '', 
        name: '', 
        description: '', 
        status: 'active',
        default_client_po_number: '',
        default_client_ticket_number: ''
      });
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Client *</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="project-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Project Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bathroom Renovation" data-testid="project-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Project details..." rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Default Client PO #</Label>
              <Input 
                value={form.default_client_po_number} 
                onChange={(e) => setForm({ ...form, default_client_po_number: e.target.value })} 
                placeholder="PO-2024-001 (optional)" 
                data-testid="project-po-input"
              />
              <p className="text-xs text-slate-500">Will auto-fill in quotes, estimates, and invoices</p>
            </div>
            <div className="space-y-2">
              <Label>Default Ticket #</Label>
              <Input 
                value={form.default_client_ticket_number} 
                onChange={(e) => setForm({ ...form, default_client_ticket_number: e.target.value })} 
                placeholder="TKT-001 (optional)" 
                data-testid="project-ticket-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="create-project-submit">
              {saving ? 'Creating...' : 'Create Project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
